/* eslint-disable @typescript-eslint/prefer-includes */
import { FC, HTMLProps, useMemo } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { useFeature } from '@optimizely/react-sdk';

import { useMerchant } from 'hooks/context/merchant';
import { convertToRecaptchaLocale, RecaptchaLocale } from 'hooks/context/ReCaptcha.util';

import { LocalePicker } from 'components/LocalePicker/LocalePicker';

import cx from 'clsx';
import styles from 'components/PageFooter.module.scss';
import { FOOTER_TEST_IDS } from 'components/PageFooter.const';

import { AppleAppStoreLink } from 'components/AppleAppStoreLink';
import { GooglePlayStoreLink } from 'components/GooglePlayStoreLink';
import { Icon } from './Icon';
import { getHeapUserId } from 'util/Analytics';
import { stringifyUrl } from 'query-string';

export const APP_FOOTER_WHITELIST = ['en-US'];
export const LOCALE_PICKER_WHITELIST = ['en-CA', 'en-US', 'fr-CA'];

export const Footer: FC<HTMLProps<HTMLElement>> = ({ className, children, ...props }) => {
  const year = new Date().getFullYear();
  const { t } = useTranslation();
  const { locale, merchant } = useMerchant();
  const reCaptchaLocale = convertToRecaptchaLocale(locale as RecaptchaLocale);
  const [isConsumerSupportEnabled] = useFeature('consumer_support_enabled', { autoUpdate: true });
  const heapUserId = getHeapUserId();
  const merchantLocale = locale || '';

  const url = useMemo(() => stringifyUrl({
    url: `${window.location.origin}/online-ordering/app${merchant?.merchantUuid ? '/menu' : ''}`,
    query: {
      merchantId: merchant?.merchantUuid,
      heapUserId,
    },
  }), [heapUserId, merchant?.merchantUuid]);

  return (
    <footer className={cx(styles.footer, className)} {...props}>
      <div className={styles.container}>
        {children}
        <section className={cx(styles.section, styles.appLegal)}>
          <ul className={styles.links}>
            <li><a href={t('footerLinks.homepage')}>{t('legal.copyright', { year })}</a></li>
            <li><a href="/consumer/terms">{t('legal.terms')}</a></li>
            <li><a href={t('footerLinks.privacy')}>{t('legal.privacy')}</a></li>
          </ul>
          <p>{t('legal.trademark')}</p>
          <p>{t('legal.other')}</p>
          <p>
          <Trans i18nKey="legal.recaptcha">
            This site is protected by reCAPTCHA and the Google
            <a href={`https://policies.google.com/privacy?hl=${reCaptchaLocale}`} target="_blank" rel="noopener noreferrer">
              Privacy Policy
            </a>
              and
            <a href={`https://policies.google.com/terms?hl=${reCaptchaLocale}`} target="_blank" rel="noopener noreferrer">
              Terms of Service
            </a>
            apply.
          </Trans>
          </p>
        </section>
        <section className={cx(styles.section, styles.apps, styles.footerCols)}>
          {(APP_FOOTER_WHITELIST.indexOf(merchantLocale) > -1) && (
            <div className={styles.appCol} data-testid={FOOTER_TEST_IDS.appCol}>
              <ul className={styles.texts}>
                <li>{t('consumerAppFooter.message')}</li>
              </ul>
              <div className={styles.badges}>
                <GooglePlayStoreLink url={url} />
                <AppleAppStoreLink />
              </div>
            </div>
          )}
          <div className={styles.localePickerCol}>
            <ul className={styles.texts}>
              <li>{t('footer.learnMoreAbout')} <a data-testid={FOOTER_TEST_IDS.seoLink} href={t('footerLinks.learnMore')}>Clover Online Ordering</a></li>
            </ul>
            <div className={styles.localePicker}>
              {isConsumerSupportEnabled && <div className={styles.consumerHelp}>
                <Icon aria-hidden className={styles.icon} name="help" />
                <a data-testid={FOOTER_TEST_IDS.customerHelp} href={'https://docs.google.com/forms/d/e/1FAIpQLSeYtrjkknLxFoRPLkbMbfdomOFAnkJXplUBhLVwnCiQ12VMbw/viewform'} target="_blank" rel="noopener noreferrer">
                  {t('footer.customerHelp')}
                </a>
              </div>}
            {(LOCALE_PICKER_WHITELIST.indexOf(merchantLocale) > -1) && (
                <LocalePicker />
                )}
            </div>
          </div>
        </section>
      </div>
    </footer>
  );
};
